import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  ChevronLeft, ShieldAlert, ShieldCheck, ImageOff, Loader2, AlertTriangle,
  Clock, Fingerprint, Trash2, Plus, CheckCircle2, XCircle, FileWarning,
  Hash, ScrollText,
} from 'lucide-react';

const TABS = [
  { key: 'queue', label: 'Review Queue', icon: Clock },
  { key: 'csam', label: 'CSAM Evidence', icon: FileWarning, adminOnly: true },
  { key: 'hashes', label: 'Blocked Hashes', icon: Hash },
  { key: 'events', label: 'Audit Log', icon: ScrollText },
];

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch (e) {
    return iso.slice(0, 16);
  }
}

export default function ModerationDashboardPage() {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';
  const isMod = isAdmin || user?.role === 'moderator';

  const [tab, setTab] = useState('queue');
  const [summary, setSummary] = useState(null);
  const [queue, setQueue] = useState([]);
  const [csam, setCsam] = useState([]);
  const [hashes, setHashes] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [actingOn, setActingOn] = useState('');
  const [newHash, setNewHash] = useState('');
  const [newHashReason, setNewHashReason] = useState('');

  const authedHeaders = useMemo(
    () => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token]
  );

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    if (!isMod) {
      navigate('/dashboard');
    }
  }, [authLoading, user, isMod, navigate]);

  useEffect(() => {
    if (!isMod) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [sumRes, qRes, hRes, eRes] = await Promise.all([
          axios.get(`${API}/admin/moderation/summary`, { headers: authedHeaders }),
          axios.get(`${API}/admin/moderation/queue`, { headers: authedHeaders }),
          axios.get(`${API}/admin/moderation/blocked-hashes`, { headers: authedHeaders }),
          axios.get(`${API}/admin/moderation/events`, { headers: authedHeaders }),
        ]);
        if (cancelled) return;
        setSummary(sumRes.data);
        setQueue(qRes.data?.cases || []);
        setHashes(hRes.data?.items || []);
        setEvents(eRes.data?.items || []);
        if (isAdmin) {
          const cRes = await axios.get(`${API}/admin/moderation/csam`, { headers: authedHeaders });
          if (!cancelled) setCsam(cRes.data?.items || []);
        }
      } catch (e) {
        console.error('[mod] load failed:', e?.response?.data || e?.message);
        if (!cancelled) setError('Could not load moderation data. ' + (e?.response?.data?.detail || ''));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [isMod, isAdmin, authedHeaders]);

  const refresh = async () => {
    try {
      const [sumRes, qRes] = await Promise.all([
        axios.get(`${API}/admin/moderation/summary`, { headers: authedHeaders }),
        axios.get(`${API}/admin/moderation/queue`, { headers: authedHeaders }),
      ]);
      setSummary(sumRes.data);
      setQueue(qRes.data?.cases || []);
    } catch (e) {
      console.warn('[mod] refresh failed:', e?.message);
    }
  };

  const approveCase = async (caseId) => {
    setActingOn(caseId);
    try {
      await axios.post(`${API}/admin/moderation/queue/${caseId}/approve`, {}, { headers: authedHeaders });
      await refresh();
    } catch (e) {
      alert('Approve failed: ' + (e?.response?.data?.detail || e?.message));
    } finally {
      setActingOn('');
    }
  };

  const rejectCase = async (caseId) => {
    const reason = window.prompt('Reason for rejecting these photos? (optional)');
    if (reason === null) return;
    setActingOn(caseId);
    try {
      await axios.post(`${API}/admin/moderation/queue/${caseId}/reject`, { reason }, { headers: authedHeaders });
      await refresh();
    } catch (e) {
      alert('Reject failed: ' + (e?.response?.data?.detail || e?.message));
    } finally {
      setActingOn('');
    }
  };

  const addHash = async () => {
    if (!newHash.trim()) return;
    try {
      await axios.post(`${API}/admin/moderation/blocked-hashes`,
        { phash: newHash.trim(), category: 'manual', reason: newHashReason.trim() || null },
        { headers: authedHeaders },
      );
      const hRes = await axios.get(`${API}/admin/moderation/blocked-hashes`, { headers: authedHeaders });
      setHashes(hRes.data?.items || []);
      setNewHash(''); setNewHashReason('');
    } catch (e) {
      alert('Add failed: ' + (e?.response?.data?.detail || e?.message));
    }
  };

  const deleteHash = async (phash) => {
    if (!window.confirm(`Delete blocked hash "${phash}"?`)) return;
    try {
      await axios.delete(`${API}/admin/moderation/blocked-hashes/${encodeURIComponent(phash)}`, { headers: authedHeaders });
      setHashes((prev) => prev.filter((h) => h.phash !== phash));
    } catch (e) {
      alert('Delete failed: ' + (e?.response?.data?.detail || e?.message));
    }
  };

  if (authLoading || !user) return null;
  if (!isMod) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <Link to="/dashboard" className="inline-flex items-center gap-1 text-slate-300 hover:text-white text-sm mb-3">
            <ChevronLeft className="w-4 h-4" /> Back to Dashboard
          </Link>
          <div className="flex items-center gap-2 mb-1">
            <ShieldAlert className="w-6 h-6 text-amber-300" />
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Moderation Cockpit</h1>
          </div>
          <p className="text-slate-300 text-sm">
            Photos held for review · CSAM quarantine · Blocked hashes · Audit log
          </p>
        </div>
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="max-w-6xl mx-auto px-4 py-4 grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="mod-summary">
          {[
            { key: 'queue_count', label: 'Awaiting review', color: 'amber', icon: Clock },
            { key: 'csam_pending_alerts', label: 'CSAM alerts', color: 'red', icon: ShieldAlert, adminOnly: true },
            { key: 'csam_total_quarantined', label: 'Quarantined total', color: 'slate', icon: FileWarning, adminOnly: true },
            { key: 'blocked_hashes', label: 'Blocked hashes', color: 'blue', icon: Hash },
            { key: 'events_last_24h', label: 'Events 24h', color: 'emerald', icon: ScrollText },
          ].filter((c) => !c.adminOnly || isAdmin).map((c) => {
            const Icon = c.icon;
            return (
              <Card key={c.key} className="border-slate-200" data-testid={`mod-summary-${c.key}`}>
                <CardContent className="p-4">
                  <div className={`inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-${c.color}-700 mb-1`}>
                    <Icon className="w-3.5 h-3.5" />
                    <span>{c.label}</span>
                  </div>
                  <div className="text-2xl font-bold text-slate-900">
                    {summary[c.key] ?? 0}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Tabs */}
      <div className="max-w-6xl mx-auto px-4 border-b border-slate-200">
        <div className="flex gap-1 overflow-x-auto">
          {TABS.filter((t) => !t.adminOnly || isAdmin).map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  tab === t.key
                    ? 'border-slate-900 text-slate-900'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
                data-testid={`mod-tab-${t.key}`}
              >
                <Icon className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Body */}
      <div className="max-w-6xl mx-auto px-4 py-5">
        {loading && (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        )}
        {!loading && error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 text-sm">{error}</div>
        )}

        {/* Review Queue */}
        {!loading && tab === 'queue' && (
          <div className="space-y-3" data-testid="mod-queue">
            {queue.length === 0 ? (
              <EmptyState icon={ShieldCheck} title="No cases awaiting review" subtitle="AI hasn't flagged any ambiguous photos in the last upload batch." />
            ) : queue.map((c) => (
              <Card key={c.case_id} className="border-amber-200 bg-amber-50/30" data-testid={`mod-queue-row-${c.case_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-slate-800">{c.full_name || 'Case'}</span>
                        <Badge className="bg-amber-100 text-amber-800 border border-amber-200 text-[10px] uppercase tracking-wider">Photo Review</Badge>
                      </div>
                      <div className="text-xs text-slate-500 space-y-0.5">
                        <div>Case ID: <span className="font-mono">{c.case_id}</span></div>
                        <div>By: {c.reporter_email || '—'} · IP: {c.reporter_ip || '—'}</div>
                        <div>Submitted: {fmtDate(c.created_at)}</div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => approveCase(c.case_id)} disabled={actingOn === c.case_id} data-testid={`mod-approve-${c.case_id}`}>
                        <CheckCircle2 className="w-4 h-4 mr-1.5" /> Approve
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => rejectCase(c.case_id)} disabled={actingOn === c.case_id} data-testid={`mod-reject-${c.case_id}`}>
                        <XCircle className="w-4 h-4 mr-1.5" /> Reject
                      </Button>
                    </div>
                  </div>
                  {c.photos && c.photos.length > 0 && (
                    <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                      {c.photos.map((p, idx) => (
                        <div key={`${c.case_id}-photo-${idx}`} className="relative">
                          <img
                            src={p}
                            alt={`Photo ${idx + 1}`}
                            className={`w-full h-24 object-cover rounded border ${(c.photo_review_indices || []).includes(idx) ? 'border-amber-500 ring-2 ring-amber-300' : 'border-slate-200'}`}
                          />
                          {(c.photo_review_indices || []).includes(idx) && (
                            <span className="absolute top-1 left-1 bg-amber-500 text-white text-[9px] uppercase px-1.5 py-0.5 rounded font-semibold">AI Flagged</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* CSAM Evidence — admin only */}
        {!loading && tab === 'csam' && isAdmin && (
          <div className="space-y-3" data-testid="mod-csam">
            <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-3 text-xs">
              <AlertTriangle className="w-4 h-4 inline mr-1" />
              These records contain evidence of illegal content. Do NOT share externally.
              Retained for 90 days per IT Rules 2021 §4(4). Actual images are not displayed here — access DB console.
            </div>
            {csam.length === 0 ? (
              <EmptyState icon={ShieldCheck} title="No CSAM incidents" subtitle="No child-safety violations have been auto-flagged." />
            ) : csam.map((q, idx) => (
              <Card key={q.hash_id || q.pdq_hash || q.case_id || `csam-${idx}`} className="border-red-200 bg-red-50/30" data-testid={`mod-csam-row-${idx}`}>
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <ShieldAlert className="w-4 h-4 text-red-600" />
                        <span className="font-semibold text-slate-800">Suspected CSAM upload</span>
                        {q.released_to_lea ? (
                          <Badge className="bg-slate-800 text-white text-[10px] uppercase tracking-wider">Released to LEA</Badge>
                        ) : (
                          <Badge className="bg-red-600 text-white text-[10px] uppercase tracking-wider">Quarantined</Badge>
                        )}
                      </div>
                      <div className="text-xs text-slate-600 space-y-0.5">
                        <div>User: <span className="font-mono">{q.user_email || q.user_id || '—'}</span></div>
                        <div>IP: {q.ip || '—'} · Photo #{q.photo_index}</div>
                        <div>Hash: <span className="font-mono">{q.phash}</span></div>
                        <div>Detected: {fmtDate(q.created_at)}</div>
                        {q.ai_scores && (
                          <div className="text-[11px]">
                            AI: {Object.entries(q.ai_scores).map(([k, v]) => `${k}=${(v * 100).toFixed(0)}%`).join(' · ')}
                          </div>
                        )}
                        {q.reason && <div className="text-slate-500 italic">&ldquo;{q.reason}&rdquo;</div>}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <Button size="sm" variant="outline" onClick={async () => {
                        try {
                          await axios.post(`${API}/admin/moderation/csam/${encodeURIComponent(q.phash)}/acknowledge`, {}, { headers: authedHeaders });
                          alert('Acknowledged.');
                        } catch (e) { alert('Ack failed: ' + (e?.response?.data?.detail || e?.message)); }
                      }} data-testid={`mod-csam-ack-${idx}`}>
                        Acknowledge
                      </Button>
                      {!q.released_to_lea && (
                        <Button size="sm" variant="destructive" onClick={async () => {
                          if (!window.confirm('Mark this record as released to Law Enforcement (NCRB)?')) return;
                          try {
                            await axios.post(`${API}/admin/moderation/csam/${encodeURIComponent(q.phash)}/mark-lea`, {}, { headers: authedHeaders });
                            const c = await axios.get(`${API}/admin/moderation/csam`, { headers: authedHeaders });
                            setCsam(c.data?.items || []);
                          } catch (e) { alert('Mark-LEA failed: ' + (e?.response?.data?.detail || e?.message)); }
                        }} data-testid={`mod-csam-lea-${idx}`}>
                          Mark released to LEA
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Blocked Hashes */}
        {!loading && tab === 'hashes' && (
          <div className="space-y-3" data-testid="mod-hashes">
            {isAdmin && (
              <Card className="border-slate-200">
                <CardContent className="p-4 flex flex-wrap items-end gap-2">
                  <div className="flex-1 min-w-[200px]">
                    <label className="text-xs text-slate-500 block mb-1">Perceptual hash (e.g. dhash:abc123…)</label>
                    <input value={newHash} onChange={(e) => setNewHash(e.target.value)} className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm font-mono" placeholder="dhash:0123456789abcdef" data-testid="mod-hash-input" />
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <label className="text-xs text-slate-500 block mb-1">Reason (optional)</label>
                    <input value={newHashReason} onChange={(e) => setNewHashReason(e.target.value)} className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm" placeholder="Why block this?" data-testid="mod-hash-reason" />
                  </div>
                  <Button size="sm" onClick={addHash} disabled={!newHash.trim()} data-testid="mod-hash-add">
                    <Plus className="w-4 h-4 mr-1.5" /> Add
                  </Button>
                </CardContent>
              </Card>
            )}
            {hashes.length === 0 ? (
              <EmptyState icon={Fingerprint} title="No hashes blocked yet" subtitle="Auto-add happens when AI flags a CSAM upload. Manual entries can be added above." />
            ) : (
              <Card className="border-slate-200">
                <CardContent className="p-0">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                      <tr>
                        <th className="text-left px-4 py-2 font-medium">Hash</th>
                        <th className="text-left px-4 py-2 font-medium">Category</th>
                        <th className="text-left px-4 py-2 font-medium">Reason</th>
                        <th className="text-left px-4 py-2 font-medium">Added</th>
                        <th className="px-4 py-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {hashes.map((h) => (
                        <tr key={h.phash} className="border-t border-slate-100" data-testid={`mod-hash-row-${h.phash}`}>
                          <td className="px-4 py-2 font-mono text-xs truncate max-w-[200px]">{h.phash}</td>
                          <td className="px-4 py-2">
                            <Badge className={h.category === 'csam' ? 'bg-red-100 text-red-700 border-red-200' : 'bg-slate-100 text-slate-700 border-slate-200'}>
                              {h.category}
                            </Badge>
                          </td>
                          <td className="px-4 py-2 text-xs text-slate-600 truncate max-w-[240px]">{h.reason || '—'}</td>
                          <td className="px-4 py-2 text-xs text-slate-500">{fmtDate(h.added_at)}</td>
                          <td className="px-4 py-2 text-right">
                            {isAdmin && h.category !== 'csam' && (
                              <Button size="sm" variant="ghost" onClick={() => deleteHash(h.phash)} data-testid={`mod-hash-del-${h.phash}`}>
                                <Trash2 className="w-4 h-4 text-red-600" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Audit log */}
        {!loading && tab === 'events' && (
          <div data-testid="mod-events">
            {events.length === 0 ? (
              <EmptyState icon={ScrollText} title="No events yet" subtitle="Moderator actions and AI-triggered events will appear here." />
            ) : (
              <Card className="border-slate-200">
                <CardContent className="p-0">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                      <tr>
                        <th className="text-left px-4 py-2 font-medium">Kind</th>
                        <th className="text-left px-4 py-2 font-medium">Actor</th>
                        <th className="text-left px-4 py-2 font-medium">Target</th>
                        <th className="text-left px-4 py-2 font-medium">When</th>
                      </tr>
                    </thead>
                    <tbody>
                      {events.map((e, idx) => (
                        <tr key={e.event_id || `${e.kind}-${e.created_at}-${idx}`} className="border-t border-slate-100">
                          <td className="px-4 py-2 text-xs">
                            <Badge className={e.kind === 'csam' ? 'bg-red-100 text-red-700 border-red-200' : e.kind === 'rejected' ? 'bg-orange-100 text-orange-700 border-orange-200' : 'bg-slate-100 text-slate-700 border-slate-200'}>
                              {e.kind}
                            </Badge>
                          </td>
                          <td className="px-4 py-2 text-xs">{e.moderator_email || e.user_email || '—'}</td>
                          <td className="px-4 py-2 text-xs font-mono">{e.case_id || e.photo_index || '—'}</td>
                          <td className="px-4 py-2 text-xs text-slate-500">{fmtDate(e.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function EmptyState({ icon: Icon, title, subtitle }) {
  return (
    <div className="text-center py-14">
      <Icon className="w-10 h-10 text-slate-300 mx-auto mb-3" />
      <h3 className="text-slate-700 font-semibold">{title}</h3>
      <p className="text-slate-500 text-sm mt-1">{subtitle}</p>
    </div>
  );
}
